// src/pages/TermsPage.js
//
// Route: /terms
// Version: v0.1 — working draft, pre-lawyer review
// Last updated: June 2026
//
// Plain-language terms. Covers:
//   - What HolaSam is and is not
//   - No professional relationship
//   - No reliance
//   - Data and privacy reference
//   - Deletion contact
//   - Governing law

import React from 'react'

const EFFECTIVE_DATE = 'June 2026'
const CONTACT_EMAIL  = 'hello@holasam.app'
const VERSION        = 'v0.1'

// ─── Section component ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <section className="legal-section">
      <h2 className="legal-h2">{title}</h2>
      {children}
    </section>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TermsPage() {
  return (
    <div className="legal-root">
      <div className="legal-col">

        <header className="legal-header">
          <a href="/" className="legal-wordmark">Hola<span>Sam</span></a>
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-meta">Effective: {EFFECTIVE_DATE} · {VERSION}</p>
          <p className="legal-meta">
            This is a working draft. It will be reviewed by a qualified lawyer before
            HolaSam reaches significant public use.
          </p>
        </header>

        <Section title="What HolaSam is">
          <p>
            HolaSam is an educational guidance tool for US persons living in Spain.
            It helps you understand the shape of your cross-border tax situation —
            what obligations may apply, what questions to ask, and when to involve
            a professional.
          </p>
          <p>
            HolaSam is <strong>not</strong> a tax filing service, a law firm, an accounting
            firm, or a licensed financial advisor. It does not provide tax advice,
            legal advice, or financial advice.
          </p>
        </Section>

        <Section title="No professional relationship">
          <p>
            Using HolaSam does not create any professional relationship between
            you and HolaSam or its operators — not an accountant-client relationship,
            not an attorney-client relationship, and not any other advisory relationship.
          </p>
          <p>
            Any output from HolaSam — including triage snapshots, assessments,
            chat responses, and any other generated content — is general educational
            information only.
          </p>
        </Section>

        <Section title="No reliance">
          <p>
            Do not file tax returns, make financial decisions, or take legal action
            based solely on anything HolaSam produces. Your situation involves
            facts, history, and circumstances that HolaSam does not fully know.
          </p>
          <p>
            For decisions that matter, consult a qualified professional: a US CPA
            with international experience, a Spanish asesor fiscal, or both.
            HolaSam can help you understand what questions to ask them.
          </p>
        </Section>

        <Section title="What we collect and how we use it">
          <p>
            We collect the information you provide — including your email address,
            tax profile data, and triage answers — to provide the service.
            We do not sell your data.
          </p>
          <p>
            If you opt in separately, we may send you occasional updates and tax
            orientation content by email. You can unsubscribe at any time.
          </p>
          <p>
            If you connect with a partner advisor through HolaSam, we share your
            triage snapshot and contact details with that specific named advisor,
            with your explicit authorisation. We log every share.
          </p>
          <p>
            Your data is processed using Supabase (database and authentication),
            Anthropic Claude (AI responses), and Stripe (payments, where applicable).
            Each is a data sub-processor operating under appropriate terms.
          </p>
          <p>
            For full details, see our <a href="/privacy" className="legal-link">Privacy Policy</a>.
          </p>
        </Section>

        <Section title="Deleting your data">
          <p>
            You can request deletion of your account and associated data at any time
            by emailing <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
            We will delete or anonymise your profile, tax data, conversations, and
            assessments. Payment records are retained as required by Spanish accounting
            law (7 years). Consent records are retained as proof of lawful processing.
          </p>
          <p>
            We aim to action deletion requests within 30 days.
          </p>
        </Section>

        <Section title="Accuracy and limitations">
          <p>
            HolaSam uses deterministic rules and AI-assisted interpretation to generate
            outputs. Both can be wrong. Tax law changes. Your facts may differ from
            what the system assumes.
          </p>
          <p>
            We do our best to keep guidance current and accurate, but we make no
            warranty — express or implied — about the accuracy, completeness, or
            fitness for purpose of any output.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p>
            To the maximum extent permitted by applicable law, HolaSam and its
            operators are not liable for any loss, damage, penalty, or filing
            consequence arising from your use of or reliance on this service.
          </p>
          <p>
            This includes losses arising from inaccurate outputs, missed deadlines,
            incorrect filings, or decisions made based on HolaSam content without
            independent professional verification.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These terms are governed by Spanish law. Any disputes will be subject
            to the jurisdiction of the Spanish courts, without prejudice to any
            mandatory consumer rights you hold under applicable EU law.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these terms as the product evolves. We will note the
            version and effective date at the top of this page. Continued use of
            HolaSam after a change constitutes acceptance of the updated terms.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these terms:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>
          </p>
        </Section>

        <footer className="legal-footer">
          <a href="/" className="legal-link">← Back to HolaSam</a>
          <span className="legal-footer-sep">·</span>
          <a href="/privacy" className="legal-link">Privacy Policy</a>
        </footer>

      </div>
    </div>
  )
}
